import { browser } from '$app/environment';
import type { ApiSchemas } from '$lib/api/shopwareApi.js';
import {
    CONTACT_FORM_DATA_STORE_KEY,
    CUSTOMER_COUPON_CODE_CONTEXT_KEY,
    SAVED_CAR_DATA_STORE_KEY
} from '$lib/context.js';
import type { ContactFormData } from '$lib/types/ContactFormData.js';
import type { SavedCarData } from '$lib/types/SavedCarData.js';
import type { ToastContainerType, ToastData } from '$lib/types/Toast.js';
import { DateTime } from 'luxon';
import { getContext, hasContext, setContext } from 'svelte';
import type { Readable } from 'svelte/store';
import { readable, writable } from 'svelte/store';

/**
 * Stores automatically shared with child components using svelte context.
 * Solves Sveltekits SSR store problems/vulnerabilities.
 *
 * @see https://dev.to/jdgamble555/the-correct-way-to-use-stores-in-sveltekit-3h6i
 * @param name
 * @param fn
 * @param defaultValue
 */
export const useSharedStore = <ValueType, StoreType extends Readable<ValueType> = Readable<ValueType>>(
    name: string,
    fn: (value?: ValueType) => StoreType,
    defaultValue?: ValueType
): StoreType => {
    if (hasContext(name)) {
        return getContext<StoreType>(name);
    }

    const store = fn(defaultValue);
    setContext(name, store);

    return store;
};

export const useWritable = <T>(name: string, value: T) => useSharedStore(name, writable, value);
export const useReadable = <T>(name: string, value: T) => useSharedStore(name, readable, value);

export const useSavedCarData = (savedCarData?: SavedCarData) =>
    useWritable(SAVED_CAR_DATA_STORE_KEY, savedCarData ?? { selectedCarId: undefined, cars: [] });

export const useCustomerCouponCode = (customerCouponCode?: ApiSchemas['LineItem']) =>
    useWritable<ApiSchemas['LineItem']>(CUSTOMER_COUPON_CODE_CONTEXT_KEY, customerCouponCode);

export const useContactFormData = (contactFormData?: ContactFormData) =>
    useWritable(
        CONTACT_FORM_DATA_STORE_KEY,
        contactFormData ?? { name: '', email: '', message: '', acceptedTerms: false }
    );

export const toast = writable<ToastContainerType>(undefined);
export const visibleToasts = writable<ToastData[]>([]);
export const toastIdCounter = writable(0);

export const checkoutCreditCardToken = writable<string>('');

export const time = readable(DateTime.now(), function start(set) {
    const interval = setInterval(() => {
        set(DateTime.now());
    }, 1000);

    return function stop() {
        clearInterval(interval);
    };
});
export const slowTime = readable(DateTime.now(), function start(set) {
    const interval = setInterval(() => {
        set(DateTime.now());
    }, 60000);

    return function stop() {
        clearInterval(interval);
    };
});

export const isMobile = readable(false, function start(set) {
    if (!browser) {
        //TODO: Guess from user agent for pre-rendering?

        return;
    }

    const mediaQuery = window.matchMedia('(max-width: 991px)');
    set(mediaQuery.matches);

    const listener = (mq: MediaQueryListEvent) => set(mq.matches);
    mediaQuery.addEventListener('change', listener);

    return () => mediaQuery.removeEventListener('change', listener);
});

export const featureFlags = writable({ person_profiles: 'identified_only' });
